import Eyebrow from "../components/Eyebrow";
import Breadcrumbs from "../components/Breadcrumbs";
import CTAButton from "../components/CTAButton";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal, KineticLines } from "../components/Reveal";
import { usePageMeta } from "../lib/seo";
import { CARE_OPTIONS, COMPARE_NOTE } from "../lib/site";

const DETAILS = {
  "board-and-care": {
    definition:
      "A smaller residential setting, often in a home-like environment, for older adults who need day-to-day assistance and may benefit from a more intimate setting and closer familiarity with staff. In California, many Board & Care homes and Assisted Living communities operate under the Residential Care Facility for the Elderly licensing framework, although their size and setting can be quite different.",
    who: "Often considered by families who want a smaller, home-like setting with closer familiarity with staff and a high level of personal attention.",
    support:
      "Day-to-day assistance in a residential home, with a more intimate living environment and a small number of residents.",
    questions: [
      "How many residents live here, and what is the staffing like day to day?",
      "What does a typical day look like?",
      "How are care plans adjusted as needs change?",
      "What's included in the monthly cost, and what isn't?",
    ],
  },
  "independent-living": {
    definition:
      "A community setting for older adults who are largely independent and want the convenience, community, and social opportunities of senior living without regular hands-on personal care as the central service.",
    who: "Often considered by older adults planning ahead, who want more community and less home maintenance while keeping their independence.",
    support:
      "Convenience, community, and social opportunities. Regular hands-on personal care is generally not the central service, and support can often be added separately if needs change.",
    questions: [
      "What does daily life and the activity calendar look like?",
      "How does support work if needs increase later on?",
      "What's included in the monthly cost?",
      "How easy is it to meet people here?",
    ],
  },
  "assisted-living": {
    definition:
      "A residential community for older adults who value independence but need regular help with some everyday activities. Services vary by community and by the person's assessed needs.",
    who: "Often considered when everyday activities would benefit from regular help, with independence supported in a community setting.",
    support:
      "Regular help with everyday activities (such as bathing, dressing, mobility, meals, or medication support within the community's licensed scope), alongside community life.",
    questions: [
      "What does a typical day look like for residents?",
      "How does the community support independence while providing help?",
      "What's included in the monthly cost, and what isn't?",
      "How are families kept informed?",
    ],
  },
  "caregiver-support": {
    definition:
      "Help provided in the person's own home with everyday activities and companionship, depending on the services arranged and the provider's permitted scope.",
    who: "Often considered when remaining at home is the preferred option and additional help with everyday activities may be needed, for the older adult's benefit, the family caregiver's, or both.",
    support:
      "In-home assistance with everyday activities and companionship, scaled to what the family arranges.",
    questions: [
      "What kinds of support are available, and how flexible is the schedule?",
      "How are caregivers vetted and trained?",
      "How will we know if home support is enough, or when a different setting should be considered?",
    ],
  },
};

const COMPARISON = {
  criteria: [
    "Typical setting",
    "Level of independence",
    "Day-to-day assistance",
    "Meals",
    "Housekeeping",
    "Social environment",
    "Typical atmosphere",
    "When a family might consider it",
  ],
  independent: [
    "Apartment-style community with shared spaces and amenities",
    "Largely self-directed",
    "Light or as needed; often arranged separately",
    "Shared dining options, often with flexible plans",
    "Often available or included",
    "Active calendars, clubs, and outings",
    "Active and social",
    "Planning ahead while independence is strong",
  ],
  assisted: [
    "Residential community with common areas and staff on site",
    "Independence with regular help available",
    "Regular help with everyday activities when needed",
    "Meals provided daily",
    "Included",
    "Daily activities sized to abilities",
    "Supportive and community-minded",
    "Everyday activities would benefit from regular help at home",
  ],
  board: [
    "A small, home-like residence in a neighborhood",
    "Independence supported closely day to day",
    "Daily, hands-on assistance and supervision",
    "Home-cooked meals shared together",
    "Included; the home is managed for residents",
    "A small-group, family-style pace",
    "Quiet, personal, home-like",
    "More personal attention in a smaller setting feels right",
  ],
};

export default function CareOptions() {
  usePageMeta({
    title: "Care Options: Board & Care, Independent Living, Assisted Living & Caregiver Support",
    description:
      "A plain-English guide to senior care options in Los Angeles: Board & Care, Independent Living, Assisted Living, and caregiver support, and how to think about which may fit your family.",
    path: "/care-options",
  });

  return (
    <>
      <section className="bg-ivory pt-[72px]">
        <div className="mx-auto max-w-site px-4 pb-16 pt-10 sm:px-6 md:pb-20 md:pt-14 lg:px-12">
          <Breadcrumbs trail={[{ label: "Care Options" }]} />
          <div className="mt-10 max-w-3xl">
            <Eyebrow>Care options</Eyebrow>
            <h1 className="mt-6 font-serif text-4xl font-medium leading-[1.15] tracking-tight text-forest sm:text-5xl lg:text-[3.6rem]">
              <KineticLines lines={[<>The right care looks different</>, <>for every person.</>]} />
            </h1>
            <Reveal delay={0.4}>
              <p className="mt-7 text-lg leading-[1.7] text-charcoal/80 md:text-xl">
                Different care settings provide different levels of independence, day-to-day support,
                and community. Understanding those differences can make it easier to know which
                options may be worth considering for your family.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-warmwhite py-16 md:py-24">
        <div className="mx-auto max-w-site px-4 sm:px-6 lg:px-12">
          <Reveal>
            <h2 className="font-serif text-2xl font-medium tracking-tight text-forest md:text-3xl">
              Comparing residential care settings
            </h2>
          </Reveal>
          <Reveal delay={0.08}>
            <div className="mt-8 overflow-x-auto rounded-2xl border border-inkline" data-testid="care-options-comparison-table">
              <table className="w-full min-w-[760px] border-collapse text-left text-[0.95rem]">
                <thead>
                  <tr className="bg-forest text-ivory">
                    <th scope="col" className="px-5 py-4 font-medium">
                      What families usually compare
                    </th>
                    <th scope="col" className="px-5 py-4 font-medium">
                      Independent Living
                    </th>
                    <th scope="col" className="px-5 py-4 font-medium">
                      Assisted Living
                    </th>
                    <th scope="col" className="px-5 py-4 font-medium">
                      Board &amp; Care
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {COMPARISON.criteria.map((c, i) => (
                    <tr key={c} className={i % 2 ? "bg-ivory/60" : "bg-warmwhite"}>
                      <th scope="row" className="px-5 py-4 font-serif font-medium text-forest">
                        {c}
                      </th>
                      <td className="px-5 py-4 text-charcoal/80">{COMPARISON.independent[i]}</td>
                      <td className="px-5 py-4 text-charcoal/80">{COMPARISON.assisted[i]}</td>
                      <td className="px-5 py-4 text-charcoal/80">{COMPARISON.board[i]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-4 max-w-3xl text-[0.9rem] leading-relaxed text-charcoal/75">
              {COMPARE_NOTE}
            </p>
            <p className="mt-2 text-[0.85rem] leading-relaxed text-charcoal/55">
              This comparison is general education, not medical advice.
            </p>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="mt-10 rounded-2xl border border-inkline bg-ivory p-8">
              <h3 className="font-serif text-2xl font-medium text-forest">
                And when home is still the right place: Caregiver Support
              </h3>
              <p className="mt-3 max-w-3xl leading-[1.7] text-charcoal/80">
                Help provided in the person's own home with everyday activities and companionship,
                depending on the services arranged and the provider's permitted scope. CareCraft can
                help families explore whether support at home could make the current situation
                workable.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-warmwhite pb-24">
        <div className="mx-auto max-w-site px-4 sm:px-6 lg:px-12">
          {CARE_OPTIONS.map((o) => {
            const d = DETAILS[o.id];
            return (
              <Reveal key={o.id}>
                <article
                  id={o.id}
                  data-testid={`care-option-block-${o.id}`}
                  className="scroll-mt-28 border-t border-inkline py-14 first:border-t-0 md:py-16"
                >
                  <div className="grid gap-8 lg:grid-cols-12">
                    <div className="lg:col-span-4">
                      <p className="font-serif text-lg text-clay" aria-hidden="true">{o.num}</p>
                      <h2 className="mt-3 font-serif text-3xl font-medium tracking-tight text-forest md:text-[2.2rem]">
                        {o.title}
                      </h2>
                      <p className="mt-4 max-w-xs font-serif italic text-charcoal/70">{o.tagline}</p>
                    </div>
                    <div className="grid gap-8 lg:col-span-5">
                      <div>
                        <p className="text-[0.7rem] font-semibold uppercase tracking-[0.16em] text-forest/60">
                          What it is
                        </p>
                        <p className="mt-3 leading-[1.7] text-charcoal/80">{d.definition}</p>
                      </div>
                      <div>
                        <p className="text-[0.7rem] font-semibold uppercase tracking-[0.16em] text-forest/60">
                          Who might consider it
                        </p>
                        <p className="mt-3 leading-[1.7] text-charcoal/80">{d.who}</p>
                      </div>
                      <div>
                        <p className="text-[0.7rem] font-semibold uppercase tracking-[0.16em] text-forest/60">
                          The support concept
                        </p>
                        <p className="mt-3 leading-[1.7] text-charcoal/80">{d.support}</p>
                      </div>
                    </div>
                    <div className="lg:col-span-3">
                      <p className="text-[0.7rem] font-semibold uppercase tracking-[0.16em] text-forest/60">
                        Questions to ask when evaluating
                      </p>
                      <ul className="mt-4 space-y-3">
                        {d.questions.map((q) => (
                          <li key={q} className="flex gap-3 leading-[1.6] text-charcoal/80">
                            <span className="mt-[0.6em] h-1.5 w-1.5 shrink-0 rounded-full bg-clay" aria-hidden="true" />
                            {q}
                          </li>
                        ))}
                      </ul>
                      <CTAButton to="/contact" variant="outline" className="mt-7" testId={`care-option-discuss-${o.id}`}>
                        Discuss Fit With CareCraft
                      </CTAButton>
                      {o.id === "board-and-care" && (
                        <Link
                          to="/board-and-care"
                          data-testid="care-option-link-board-and-care"
                          className="mt-4 inline-flex items-center gap-2 text-[0.88rem] font-medium text-forest transition-colors hover:text-clay"
                        >
                          Explore Board &amp; Care Homes
                          <ArrowRight className="h-4 w-4" aria-hidden="true" />
                        </Link>
                      )}
                    </div>
                  </div>
                </article>
              </Reveal>
            );
          })}
        </div>
      </section>

      <section className="grain bg-forest py-20 text-ivory md:py-24">
        <div className="mx-auto max-w-site px-4 sm:px-6 lg:px-12">
          <Reveal>
            <div className="flex flex-col items-start justify-between gap-8 md:flex-row md:items-center">
              <div>
                <h2 className="max-w-xl font-serif text-3xl font-medium leading-tight md:text-4xl">
                  Still deciding which of these fits? That's what the first conversation is for.
                </h2>
                <p className="mt-4 max-w-xl text-ivory/80">
                  Share what's happening, and we'll help you understand which options may make sense.
                </p>
              </div>
              <CTAButton to="/contact" variant="ivory" testId="care-options-final-cta">
                Talk With Doris
              </CTAButton>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
